import React, { useState } from 'react';
import { Info, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import { canViewSql } from '@/constants/sqlViewerAccess';
import type { DiretoriaQuerySource } from '@/hooks/useDiretoriaSqlCatalog';

const SOURCE_LABEL: Record<DiretoriaQuerySource, string> = {
  erp: 'ERP FlyStart',
  cobranca: 'Cobrança (MySQL / Prisma)',
  oficina: 'Oficina (MySQL / Prisma)',
};

const SOURCE_HINT: Record<DiretoriaQuerySource, string> = {
  erp: 'Consulta executada no ERP FlyStart via API de SQL.',
  cobranca: 'Equivalente SQL documentado; dados obtidos via Prisma no endpoint /reports/dashboard.',
  oficina: 'Dados operacionais da oficina (espelho ERP, app mobile e agendamentos).',
};

export interface DiretoriaSqlButtonProps {
  sql?: string | null;
  source?: DiretoriaQuerySource;
  /** Título do modal (padrão: Consulta SQL). */
  title?: string;
  className?: string;
  stopPropagation?: boolean;
}

export const DiretoriaSqlButton: React.FC<DiretoriaSqlButtonProps> = ({
  sql,
  source,
  title = 'Consulta SQL',
  className,
  stopPropagation = false,
}) => {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!canViewSql(user?.email)) return null;
  if (!sql?.trim()) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sql.trim());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
            e.preventDefault();
          }
          setOpen(true);
        }}
        className={cn(
          'inline-flex items-center justify-center rounded-full p-0.5',
          'text-muted-foreground hover:text-primary hover:bg-muted transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className
        )}
        aria-label={`Ver consulta SQL: ${title}`}
        title="Ver consulta SQL"
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={title} size="lg">
        <div className="space-y-4 text-left">
          {source ? (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Fonte</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="inline-flex rounded bg-muted px-2 py-0.5 text-xs font-medium text-foreground mr-2">
                  {SOURCE_LABEL[source]}
                </span>
                {SOURCE_HINT[source]}
              </p>
            </div>
          ) : null}

          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <h4 className="text-sm font-semibold text-foreground">SQL</h4>
              <Button type="button" variant="outline" size="sm" onClick={() => void handleCopy()}>
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 mr-1" /> Copiado
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copiar SQL
                  </>
                )}
              </Button>
            </div>
            <pre className="text-xs bg-slate-900 text-slate-100 dark:bg-slate-950 p-3 rounded-md overflow-x-auto whitespace-pre-wrap font-mono max-h-[min(60vh,24rem)] overflow-y-auto">
              {sql.trim()}
            </pre>
          </div>
        </div>
      </Modal>
    </>
  );
};

/** @deprecated Use DiretoriaSqlButton */
export const DiretoriaSqlTooltip = DiretoriaSqlButton;
